import { CheckCircle2 } from "lucide-react";
import React from "react";

import type * as ozon from "@/api/ozonSupply";
import { cn } from "@/lib/utils";
import { plural } from "../format";
import type { OzonStage } from "./ozonStage";

/** Подписи строки направления: состояние разговора с площадкой, грузоместа и цикл 1С. */

const AMBER =
  "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300";

/** Состояния разговора с площадкой — таблицей, а не лестницей `if`. Тексты и подсказки тут
 *  содержательные: «черновик снят» ничего не говорило тому, кто не писал этот код. */
const STATES: Record<string, { cls: string; label: string; title?: string }> = {
  drafted: {
    cls: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    label: "площадка ответила",
    title:
      "Площадка ответила, сколько возьмёт. Ответ живёт ~25 минут: по нему даются окна приёмки и создаётся заявка.",
  },
  ordering: { cls: AMBER, label: "заявка отправлена — проверьте кабинет" },
  ordered: {
    cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    label: "заявка создана",
  },
  refused: { cls: AMBER, label: "не везут сюда" },
  failed: {
    cls: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300",
    label: "сорвалось",
  },
  "": { cls: "bg-muted text-muted-foreground", label: "не спрашивали" },
};

const STALE = {
  cls: AMBER,
  label: "ответ устарел",
  title:
    "Ответ площадки устарел: спросите заново, иначе окна и заявка не дадутся.",
};

/** Состояние разговора с площадкой.
 *
 *  «Черновик снят» ничего не говорило тому, кто не писал этот код: висит на каждой строке и
 *  выглядит как «всё готово». На самом деле это значит ровно одно — площадка ответила, сколько
 *  возьмёт, и её ответ живёт минут двадцать пять. Протух — так и пишем, потому что дальше ни окон,
 *  ни заявки по нему не дадут.
 */
export function StateBadge({
  state,
  stale,
}: {
  state: string;
  stale?: boolean;
}): React.ReactElement {
  const badge =
    state === "drafted" && stale ? STALE : (STATES[state] ?? STATES[""]!);
  return (
    <Badge
      cls={badge.cls}
      title={badge.title}
      icon={
        badge === STATES.drafted ? (
          <CheckCircle2 className="size-3" />
        ) : undefined
      }
    >
      {badge.label}
    </Badge>
  );
}

/** Стадия ОДНИМ бейджем — для компактной строки списка.
 *
 *  ⚠️ В списке бейдж ровно один: пока их было три (состояние площадки, грузоместа, 1С), строка
 *  расползалась на два ряда, а «где эта поставка» всё равно приходилось собирать глазами.
 *  Отказ площадки и срыв вызова стадия не различает (работа у них одна — спросить заново), а
 *  человеку в списке разница важна: «не везут сюда» — это не «ещё не спрашивали». Поэтому у
 *  стадии `ask` подпись берётся из состояния.
 */
const STAGES: Record<OzonStage, { cls: string; label: string }> = {
  ask: { cls: STATES[""]!.cls, label: "черновик не снят" },
  draft: { cls: STATES.drafted!.cls, label: "площадка ответила" },
  draft_stale: { cls: AMBER, label: "ответ устарел" },
  ordering: { cls: AMBER, label: "заявка отправлена" },
  order: { cls: STATES.ordered!.cls, label: "заявка создана" },
  layout: { cls: STATES[""]!.cls, label: "разложено, не отправлено" },
  layout_partial: { cls: AMBER, label: "места отправлены не все" },
  sent: { cls: STATES.ordered!.cls, label: "места на площадке" },
  onec_partial: { cls: AMBER, label: "1С не дописана" },
  assembly: { cls: STATES.ordered!.cls, label: "готово к сборке" },
  change: { cls: AMBER, label: "идёт правка состава" },
};

export function StageBadge({
  stage,
  state,
}: {
  stage: OzonStage;
  /** слово площадки: у стадии `ask` за ним прячутся «не везут сюда» и «сорвалось» */
  state: string;
}): React.ReactElement {
  const badge =
    stage === "ask" && (state === "refused" || state === "failed")
      ? STATES[state]!
      : STAGES[stage];
  return <Badge cls={badge.cls}>{badge.label}</Badge>;
}

/** Грузоместа: отправлены или нет. Это и есть светофор для документов 1С — их пишут ТОЛЬКО по
 *  подтверждённому площадкой составу (`ozon_onec.ready`), и узнавать об этом из отказа кнопки
 *  менеджеру незачем. Пока раскладки нет, бейджа нет: пустая строка «мест 0» ничего не добавляет. */
export function PlacesBadge({
  total,
  sent,
}: {
  total: number;
  sent: number;
}): React.ReactElement | null {
  if (total <= 0) return null;
  // тем же соглашением, что и `STATES` выше: состояние выбирается выражением, рисуется один Badge
  const badge =
    sent === 0
      ? {
          cls: STATES[""]!.cls,
          title:
            "Раскладка есть, но на площадку не уехала: документы 1С пишутся только по подтверждённому составу.",
          label: `разложено ${plural(total, "место", "места", "мест")}, не отправлены`,
        }
      : sent < total
        ? {
            cls: AMBER,
            title:
              "Отправлены не все места — дошлите остальные: документы 1С пишутся по составу целиком.",
            label: `грузоместа ${sent} из ${total}`,
          }
        : {
            cls: STATES.ordered!.cls,
            title:
              "Все места стоят на площадке и состав подтверждён — можно создавать документы 1С.",
            label: "грузоместа отправлены",
          };
  return (
    <Badge
      cls={badge.cls}
      title={badge.title}
      icon={sent === total ? <CheckCircle2 className="size-3" /> : undefined}
    >
      {badge.label}
    </Badge>
  );
}

/** Где цикл 1С — одной подписью в строке направления.
 *
 *  ⚠️ Не украшение. Товар уходит из свободного остатка (и из следующего распределения) ровно
 *  в момент проведения ПТМО: она переводит ГП на юрлицо, а юрлиц-получателей в снимке остатков
 *  нет. Пока бронь товара не сделана, «можно ли начинать следующее распределение» решают глазами
 *  по этой подписи — и после перезагрузки страницы она обязана быть на месте, а не только в
 *  ответе на нажатие «Записать в 1С».
 *
 *  Показываем ЗАКАЗ ПОКУПАТЕЛЯ: по нему в учёте ищут поставку, к нему прикреплены сборочный лист
 *  и этикетки, и его же спрашивают в разговоре. ПТМО остаётся признаком «товар уже переведён на
 *  юрлицо» — это меняет цвет и подсказку, но не номер.
 */
export function OnecMark({
  onec,
}: {
  onec: ozon.OzonOnec;
}): React.ReactElement {
  const moved = onec.transfer_number !== null;
  const order = onec.order_number;
  return (
    <span
      className={cn(
        "text-xs",
        moved
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-amber-600 dark:text-amber-400",
      )}
      title={
        moved
          ? `Заказ покупателя ${order}. ПТМО ${onec.transfer_number} проведена — товар переведён на юрлицо и из общего остатка ушёл`
          : `Заказ покупателя ${order}. Цикл 1С остановился на шаге «${onec.state}»: ПТМО ещё нет, товар числится на общем складе`
      }
    >
      {moved ? `1С: заказ ${order}` : `1С: заказ ${order} — ПТМО нет`}
    </span>
  );
}

/** Что записано в 1С — блоком, а не подписью: под раскладкой и на своей секции страницы. */
export function OnecBanner({
  onec,
}: {
  onec: ozon.OzonOnec;
}): React.ReactElement {
  return (
    <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
      1С: заказ <b>{onec.order_number}</b>
      {onec.pallets && (
        <>
          {" "}
          · паллеты <b>{onec.pallets}</b>
        </>
      )}
      {onec.transfer_number && (
        <>
          {" "}
          · ПТМО <b>{onec.transfer_number}</b>
        </>
      )}
      {onec.invoice_number && (
        <>
          {" "}
          · счёт-фактура <b>{onec.invoice_number}</b>
        </>
      )}
      {onec.state !== "done" && <> · остановились на шаге «{onec.state}»</>}
    </div>
  );
}

function Badge({
  cls,
  icon,
  title,
  children,
}: {
  cls: string;
  icon?: React.ReactNode;
  title?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      title={title}
      className={cn(
        "flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]",
        cls,
      )}
    >
      {icon}
      {children}
    </span>
  );
}
